import React, { useState, useEffect } from 'react';
import { usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Shield,
  ShieldCheck,
  UserCog,
  Pencil,
  Trash2,
  Loader2,
  UserX,
  UserCheck,
  Crown,
  Building2
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Amministratore', icon: Crown, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
  { value: 'amministrazione', label: 'Amministrazione', icon: Building2, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
  { value: 'coordinator', label: 'Coordinator', icon: ShieldCheck, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  { value: 'pm', label: 'Project Manager', icon: Shield, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  { value: 'risorsa', label: 'Risorsa', icon: Users, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
];

const getRoleConfig = (ruolo) => {
  return ROLE_OPTIONS.find(r => r.value === ruolo) || ROLE_OPTIONS[3];
};

export const GestioneUtentiPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({ ruolo: '', is_active: true });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      ruolo: user.ruolo,
      is_active: user.is_active
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    try {
      // Update role if changed
      if (editForm.ruolo !== selectedUser.ruolo) {
        await usersAPI.updateRole(selectedUser.id, editForm.ruolo);
      }
      
      // Update status if changed
      if (editForm.is_active !== selectedUser.is_active) {
        await usersAPI.updateStatus(selectedUser.id, editForm.is_active);
      }
      
      toast.success('Utente aggiornato con successo');
      setIsEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    try {
      await usersAPI.delete(selectedUser.id);
      toast.success('Utente eliminato');
      setIsDeleteDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickToggleStatus = async (user) => {
    try {
      await usersAPI.updateStatus(user.id, !user.is_active);
      toast.success(user.is_active ? 'Utente disattivato' : 'Utente attivato');
      loadUsers();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  if (currentUser?.ruolo !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Non hai i permessi per accedere a questa pagina</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="gestione-utenti-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestione-utenti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserCog className="h-7 w-7" />
            Gestione Utenti
          </h1>
          <p className="text-muted-foreground">Assegna ruoli e gestisci i permessi degli utenti</p>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Totale: <strong>{users.length}</strong> utenti</span>
          <span>Attivi: <strong>{users.filter(u => u.is_active).length}</strong></span>
        </div>
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Legenda Ruoli</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {ROLE_OPTIONS.map((role) => (
              <div key={role.value} className="flex items-center gap-2">
                <Badge className={role.color}>
                  <role.icon className="h-3 w-3 mr-1" />
                  {role.label}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utente</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const roleConfig = getRoleConfig(user.ruolo);
                const isCurrentUser = user.id === currentUser?.id;
                
                return (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${roleConfig.color}`}>
                          <roleConfig.icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {user.nome} {user.cognome}
                            {isCurrentUser && (
                              <span className="text-xs text-muted-foreground ml-2">(tu)</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={roleConfig.color}>
                        {roleConfig.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                          <UserCheck className="h-3 w-3 mr-1" />
                          Attivo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-600 border-red-600">
                          <UserX className="h-3 w-3 mr-1" />
                          Disattivato
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isCurrentUser && (
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleQuickToggleStatus(user)}
                            title={user.is_active ? 'Disattiva' : 'Attiva'}
                            data-testid={`toggle-status-${user.id}`}
                          >
                            {user.is_active ? (
                              <UserX className="h-4 w-4 text-amber-500" />
                            ) : (
                              <UserCheck className="h-4 w-4 text-emerald-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(user)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Utente</DialogTitle>
            <DialogDescription>
              {selectedUser?.nome} {selectedUser?.cognome} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select
                value={editForm.ruolo}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, ruolo: value }))}
              >
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <span className="flex items-center gap-2">
                        <role.icon className="h-4 w-4" />
                        {role.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Il ruolo determina i permessi e le funzionalità accessibili
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Stato Account</Label>
                <p className="text-xs text-muted-foreground">
                  Un utente disattivato non può accedere al sistema
                </p>
              </div>
              <Switch
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_active: checked }))}
                data-testid="edit-status-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateUser} disabled={isSubmitting} data-testid="save-user-btn">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva Modifiche'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.nome} {selectedUser?.cognome}</strong>?
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              disabled={isSubmitting}
              data-testid="confirm-delete-btn"
            >
              {isSubmitting ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GestioneUtentiPage;
